import {
  FeatureName,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useUpdateAlertsStatus,
} from '@gripp/shared-logic';
import {
  AlertsManagementList,
  AlertsManagementTableData,
  Colors,
  EditAlertModal,
  SlideLayout,
} from '@gripp/shared-ui';
import React, { useCallback, useState } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { AlertsManagement as AlertsManagementOld } from './alertsManagement-old';
import { AlertsManagementFilters } from './alertsManagementFilters';
import { AlertsStatusButton } from './alertsStatusButton';
import { EditAlertMenu } from './editAlertMenu';
import { useWorkflow } from './useWorkflow';

export const AlertsManagement = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <AlertsManagementBase />;
  }

  return <AlertsManagementOld />;
};

export const AlertsManagementBase = ({
  rightColumn,
  viewMode,
}: {
  rightColumn?: any;
  viewMode?: any;
}) => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 228);
  const workspace = useCurrentWorkspace();
  const [alertSummaryId, setAlertSummaryId] = useState<string>('');
  const [editAlertModalVisible, setEditAlertModalVisible] = useState(false);
  const [editAlertModalStyle, setEditAlertModalStyle] = useState<
    StyleProp<ViewStyle>
  >(styles.hidden);
  const { workflow } = useWorkflow(alertSummaryId);
  const { mutateAsync: updateAlertsStatus, isPending: isUpdating } =
    useUpdateAlertsStatus();

  const onRowClick = useCallback((alert: AlertsManagementTableData) => {
    const alertId = alert?.id;
    if (!alertId) return;
    setAlertSummaryId(alertId);
    handleShowEditAlertModal();
  }, []);

  const handleShowEditAlertModal = useCallback(() => {
    setEditAlertModalVisible(true);
    setEditAlertModalStyle(styles.webModalBackground);
  }, []);

  const handleCloseEditAlertModal = useCallback(() => {
    setEditAlertModalVisible(false);
    setEditAlertModalStyle(styles.hidden);
    setAlertSummaryId('');
  }, []);

  const handleStatusChange = useCallback(
    async (
      alertIds: string[],
      isEnableAction: boolean,
      onStatusChange: () => void
    ) => {
      try {
        await updateAlertsStatus({
          input: {
            alertIds,
            disabled: !isEnableAction,
          },
        });
        onStatusChange();
      } catch (error) {
        console.error('Failed to update alert status:', error);
      }
    },
    [updateAlertsStatus]
  );

  const renderAlertsStatusButton = useCallback(
    ({
      alertIds,
      isEnableAction,
      onStatusChange,
    }: {
      alertIds: string[];
      isEnableAction: boolean;
      onStatusChange: () => void;
    }) => (
      <AlertsStatusButton
        isEnableAction={isEnableAction}
        onPress={() =>
          handleStatusChange(alertIds, isEnableAction, onStatusChange)
        }
        disabled={isUpdating}
      />
    ),
    [handleStatusChange, isUpdating]
  );

  if (!workspace) {
    return null;
  }

  const filterComponent = <AlertsManagementFilters />;

  const columnComponent = (
    <View style={styles.tableContainer}>
      <AlertsManagementList
        maxHeight={listMaxHeight}
        editAlertMenu={EditAlertMenu}
        onRowClick={onRowClick}
        alertsStatusButton={renderAlertsStatusButton}
      />

      {!!alertSummaryId &&
        workflow?.workspaceAsset?.id &&
        workflow.trigger?.config?.workspaceRoutineId && (
          <EditAlertModal
            editAlertModalVisible={editAlertModalVisible}
            onClose={handleCloseEditAlertModal}
            workspaceAssetId={workflow.workspaceAsset.id}
            routineId={workflow.trigger.config.workspaceRoutineId}
            alertId={alertSummaryId}
            editAlertModalStyle={editAlertModalStyle}
          />
        )}
    </View>
  );

  return (
    <SlideLayout
      leftColumn={{
        title: 'alertsManagement.menu.title',
        mainComponent: columnComponent,
        headerComponent: filterComponent,
        disableScroll: true,
      }}
      rightColumn={rightColumn}
      viewMode={viewMode}
    />
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 44,
    marginBottom: 20,
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  tableContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  hidden: {
    height: 0,
    width: 0,
    maxHeight: 0,
    minHeight: 0,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  webModalBackground: {
    flex: 1,
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
